import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { EssDialogService } from 'ngx-essentia';
import { NGXLogger as LoggerService } from "ngx-logger";
import { ResponsiveService } from '../../../shared/services/responsive.service';
import { RouteContextService } from '../../../shared/services/route-context.service';
import { TranslationGeneralService } from '../../../shared/translation/translation.general.service';
import { UIMesageService } from '../../../shared/services/ui-message.service';
import { ClipboardService } from '../../../shared/services/clipboard.service';
import { UserService } from '../../../user/user.service';
import type { SharePollDialogComponent } from '../share-poll/share-poll-dialog/share-poll-dialog.component';
import { TemplateService } from '../../core/poll-templates/template.service';
import { CurrentPollService, RoleChange } from '../../core/current-poll.service';
import { EDIT_ROLES } from '../../core/access-grant.service';
import { NavigationService } from '../../core/navigation.service';
import { PathService } from '../../core/path.service';
import { PollService } from '../../core/poll.service';
import { PollResultsService } from '../../core/poll-results.service';
import { AuthService } from '../../../user/auth.service';
import { GetAccessDialogComponent } from '../get-poll-access/accees-dialog/access-dialog.component';
import { CommonModule } from '@angular/common';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { BdcWalkModule } from 'bdc-walkthrough';
import { TranslateTemplatePipe } from '../../core/poll-templates/template-label/template-label.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICalService } from '../i-cal/ical.service';
import { AddToCalendarMenuItemsComponent } from '../i-cal/add-to-calendar-menu/add-to-calendar-menu-items.component';
import { ActionBarService } from '../../core/action-bar.service';
import { AddOptionButton } from '../options/add-option-button/add-option-button.component';
import { HeaderService } from '../poll-header/header.service';
import { SharePanelStateService } from '../share-poll/share-panel-state.service';

const CALENDAR_EXPORT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"></rect><path d="M3 9h18M8 3v4M16 3v4"></path><path d="M12 11.5v5M12 16.5l-2.2-2.2M12 16.5l2.2-2.2"></path></svg>`;

/**
 * The share dialog lives in its own lazy chunk (My Events loads it the same way): statically imported here it pulled
 * dialog, url field, address editor and the qrcode library into the initial bundle, which has only a few kB left
 * below its 2.5 MB error budget (workorder custom-participant-links S3, report §budget). ngOnInit prefetches the
 * chunk, so the click usually opens the dialog synchronously and its auto copy stays inside the click gesture.
 */
let sharePollDialogComponent: typeof SharePollDialogComponent | null = null;
let sharePollDialogLoading: Promise<typeof SharePollDialogComponent> | null = null;
function loadSharePollDialog(): Promise<typeof SharePollDialogComponent> {
    sharePollDialogLoading ??= import('../share-poll/share-poll-dialog/share-poll-dialog.component')
        .then(module => sharePollDialogComponent = module.SharePollDialogComponent)
        .catch(error => { sharePollDialogLoading = null; throw error; });
    return sharePollDialogLoading;
}

@Component({
    selector: 'context-action-bar',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatDividerModule,
        BdcWalkModule,
        TranslateTemplatePipe,
        MatTooltipModule,
        AddOptionButton,
        AddToCalendarMenuItemsComponent,
    ],
    templateUrl: './context-action-bar.component.html',
    styleUrls: ['./context-action-bar.component.scss'],
    //  changeDetection: ChangeDetectionStrategy.OnPush,

})
export class ContextActionBarComponent implements OnInit, OnDestroy {
    private roleChangedSubscription: Subscription | null = null;
    constructor(
        private uiMessage: UIMesageService,
        private logger: LoggerService,
        public dialog: MatDialog,
        public pollResultsService: PollResultsService,
        public pathService: PathService,
        public pollService: PollService,
        public routeContextService: RouteContextService,
        public userService: UserService,
        public currentPollService: CurrentPollService,
        public navigationService: NavigationService,
        public trans: TranslationGeneralService,
        public responsiveService: ResponsiveService,
        private dialogService: EssDialogService,
        private clipboardService: ClipboardService,
        public authService: AuthService,
        public icalService: ICalService,
        public templateService: TemplateService,
        public contextActionBarService: ActionBarService,
        public headerService: HeaderService,
        private sharePanelState: SharePanelStateService,
        iconRegistry: MatIconRegistry,
        sanitizer: DomSanitizer

    ) {
        iconRegistry.addSvgIconLiteral('calendar-export', sanitizer.bypassSecurityTrustHtml(CALENDAR_EXPORT_ICON));
    }

    /** pollResponses is the only route with two share targets (vote link + result
     *  matrix) — there the share button expands into a menu instead of acting directly. */
    get isPollResponses(): boolean {
        return this.routeContextService.id === 'pollResponses';
    }

    get isClosed(): boolean {
        return this.headerService.headerClass?.isClosed === true;
    }

    /**
     * Der Hint erklärt die Verwaltungs-Toolbar (Edit, Schließen, Kalender, Export).
     * Er gehört ausschließlich auf votePreview: dort landet der Owner zuerst und dort
     * ist die Sidebar-Edit-Funktion am leichtesten zu übersehen. pollResponses
     * (Dashboard) und votePreviewResponse (Bestätigungsseite) zeigen ihn nie.
     *
     * An das Share-Panel gekoppelt: solange die Panel-Karte auf dem Schirm ist, feuert
     * der Coachmark nicht — nie zwei Elemente gleichzeitig. Beim Schließen des Panels
     * schaltet `[enabled]` reaktiv scharf.
     */
    get showActionBarHint(): boolean {
        return this.routeContextService.id === 'votePreview'
            && !this.sharePanelState.isPanelVisible;
    }

    /**
     * Exactly one share entry point at a time: while the share panel is on screen
     * (votePreview, owner, not closed) the bar keeps its share button and its share
     * menu entry out of the way.
     */
    get showShareEntry(): boolean {
        return !this.sharePanelState.isPanelVisible;
    }

    public async togglePollClosed() {
        if (this.isClosed) {
            await this.headerService.reopenPoll();
        } else {
            await this.headerService.closePoll();
        }
    }

    ngOnInit(): void {
        this.logger.log('onInit', this.routeContextService.id);
        // prefetch, not awaited: a failed chunk load is retried on the click
        loadSharePollDialog().catch(error => this.logger.warn('context-action-bar: share dialog not prefetched', error?.message));
        this.roleChangedSubscription = this.currentPollService.roleChanged$.subscribe(change => this.handleRoleChanged(change));
    }

    ngOnDestroy(): void {
        this.roleChangedSubscription?.unsubscribe();
        this.roleChangedSubscription = null;
    }

    /**
     * The bar sits on the owner pages (poll-responses, vote-preview, vote-preview-response), which are
     * route gated, not role gated. Whoever loses the right to edit while the page is open (editKey rotated,
     * logout in another tab, delegation withdrawn) is sent to the vote page with the same warning the auth
     * guard gives on a reload - without the reload (workorder-poll-scoped-access-1-6, task 1). A role that
     * rises (grant accepted) or moves between edit roles changes nothing here.
     */
    handleRoleChanged(change: RoleChange): void {
        if (!EDIT_ROLES.includes(change.from) || EDIT_ROLES.includes(change.to)) {
            return;
        }
        this.logger.warn('ContextActionBarComponent: edit role lost, leaving the owner page', change.from, change.to, change.cause);
        this.uiMessage.warning(this.trans.translate('noAccess'));
        this.navigationService.navigateVote(this.currentPollService.ownerId, this.currentPollService.pollId);
    }


    /** up navigation: the arrow always leads to My Events, deterministically.
     *  History back stays with the browser — inside the poll the preview and
     *  dashboard buttons already cover switching views.
     */
    public navigateUp() {
        this.navigationService.navigateMyPolls();
    }


    /**
     * Dates of the whole poll for the calendar action (ICalService / calendarEventsFor, scope `all`): exactly one →
     * the bar shows the add-to-calendar menu, otherwise handleClickCalendar (all as .ics, or the "no dates" dialog).
     */
    get calendarEventCount(): number {
        return this.icalService.eventsForScope('all').length;
    }

    handleClickCalendar() {
        if (this.icalService.hasPollDateEvents) {
            this.icalService.downloadAllEvents();
        } else {
            this.logger.log('no dates')
            import(
                '../../../shared/ok-cancel-dialog/ok-cancel-dialog.component'
            ).then((m) => {
                const dialogRef = this.dialogService.openSmall(
                    m.OkCancelDialogComponent,
                    {
                        title: this.templateService.getLabel('iCal', 'noEventsDialogTitle'),
                        description3: this.templateService.getLabel('iCal', 'noEventsDialogMessage'),
                        okLabel: this.templateService.getLabel('iCal', 'noEventsDialogOkLabel'),
                    }
                );
            });
        }
    }

    public sharePoll(): void {
        // On votePreview this button only exists while the panel is closed — it is the
        // way back to the panel, not a second, competing share surface.
        if (this.routeContextService.id === 'votePreview') {
            this.sharePanelState.showPanel();
            return;
        }
        // No dialog data: SharePollDialogComponent does not inject MAT_DIALOG_DATA,
        // the share component resolves poll url and title itself.
        // 640px: link column plus the inline qr code (openSmall's 500px squeezes it)
        if (sharePollDialogComponent) {
            this.dialogService.open(sharePollDialogComponent, undefined, '640px');
            return;
        }
        loadSharePollDialog()
            .then(component => this.dialogService.open(component, undefined, '640px'))
            .catch(error => this.logger.error('context-action-bar: share dialog could not be loaded', error));
    }

    /**
     * The matrix url is only worth sharing while participants may read the votes list -
     * with showParticipants off the rules deny that read and a recipient would land on an
     * empty matrix (`!== false` semantics as in the rules: a missing field means open).
     */
    get showShareResultMatrix(): boolean {
        return this.headerService.headerClass?.showParticipants !== false;
    }

    /** toast only for a real copy (workorder 1.8) */
    public async shareResult(): Promise<void> {
        if (await this.clipboardService.copy(this.navigationService?.getUrlPollMatrix())) {
            this.uiMessage.success(this.trans.translate('poll.urlCopied'));
        }
    }

    public async openPollOverview() {
        this.navigationService.navigatePollResponses();
    }

    public async editPoll() {
        this.logger.log('editPoll');
        this.navigationService.navigateVotePreview(this.currentPollService.ownerId, this.currentPollService.pollId, this.currentPollService.voteUserId, true);
    }

    public async openVotePage() {
        this.navigationService.navigateVotePreview();
    }

    public async deletePoll() {
        const { OkCancelDialogComponent } = await import(
            '../../../shared/ok-cancel-dialog/ok-cancel-dialog.component'
        );
        this.logger.log('deletePoll: ');
        const dialogRef = this.dialogService.openSmall(OkCancelDialogComponent,
            {
                title: 'Delete Poll',
                description1: '',
                description3: 'Do you want to continue?',
                okLabel: 'Delete',
                cancelLabel: 'Cancel'
            });

        dialogRef.afterClosed().subscribe(async result => {
            console.log('The dialog was closed');
            if (result) {
                this.logger.log('deletePoll ');
                // delete first, navigate second — the my-polls list loads on init and
                // would otherwise race the still running delete
                await this.pollService.deletePoll(this.currentPollService.ownerId, this.currentPollService.pollId);
                this.navigationService.navigateMyPolls();
            } else {
                this.logger.log('Canceled');
            }
        });
    }


    public getAccessDialog(): void {
        this.dialogService.open(GetAccessDialogComponent, null);
    }

}
